import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Badge } from "../../ui/badge";
import { X, UserRound, FlaskConical, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface EngagementTag {
  name: string;
  type: "client" | "test" | "custom";
}

export interface EngagementTaggingResult {
  tags: EngagementTag[];
  clientIdentifier: string;
  engagementName?: string;
}

interface EngagementTaggingDialogProps {
  isOpen: boolean;
  onConfirm: (result: EngagementTaggingResult) => void;
  onClose?: () => void;
  activityName?: string;
  initialTags?: EngagementTag[];
  initialClientIdentifier?: string;
  /** Label for the confirm button. Defaults to t("start_engagement") */
  confirmLabel?: string;
  /** If provided, shows an engagement name input field */
  initialEngagementName?: string;
  /** Whether to show the engagement name input. Default: false */
  showEngagementNameField?: boolean;
  /** Whether to show mentor-only options (Running for client, Test run, Custom tags). Default: true */
  showMentorOptions?: boolean;
}

export const EngagementTaggingDialog: React.FC<
  EngagementTaggingDialogProps
> = ({
  isOpen,
  onConfirm,
  onClose,
  activityName,
  initialTags,
  initialClientIdentifier,
  confirmLabel,
  initialEngagementName,
  showEngagementNameField = false,
  showMentorOptions = true,
}) => {
  const { t } = useTranslation();
  const [isForClient, setIsForClient] = useState(false);
  const [clientName, setClientName] = useState("");
  const [isTestRun, setIsTestRun] = useState(false);
  const [customTagInput, setCustomTagInput] = useState("");
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [engagementName, setEngagementName] = useState("");

  const getDefaultEngagementName = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = now.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${activityName || "Engagement"} - ${dateStr} ${timeStr}`;
  };

  // Pre-fill from existing tags when dialog opens
  useEffect(() => {
    if (isOpen && initialTags && initialTags.length > 0) {
      const clientTag = initialTags.find((t) => t.type === "client");
      setIsForClient(!!clientTag);
      setClientName(initialClientIdentifier || clientTag?.name || "");
      setIsTestRun(initialTags.some((t) => t.type === "test"));
      setCustomTags(
        initialTags
          .filter((t) => t.type === "custom")
          .map((t) => t.name),
      );
    } else if (isOpen) {
      // Reset when opening fresh
      setIsForClient(false);
      setClientName("");
      setIsTestRun(false);
      setCustomTags([]);
    }
    setCustomTagInput("");
    setEngagementName(initialEngagementName || getDefaultEngagementName());
  }, [isOpen, initialTags, initialClientIdentifier, initialEngagementName]);

  const handleAddCustomTag = useCallback(() => {
    const tag = customTagInput.trim();
    if (tag && !customTags.includes(tag)) {
      setCustomTags((prev) => [...prev, tag]);
      setCustomTagInput("");
    }
  }, [customTagInput, customTags]);

  const handleRemoveCustomTag = useCallback((tag: string) => {
    setCustomTags((prev) => prev.filter((t) => t !== tag));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddCustomTag();
      }
    },
    [handleAddCustomTag],
  );

  const handleConfirm = useCallback(() => {
    const tags: EngagementTag[] = [];

    if (isForClient) {
      tags.push({
        name: clientName.trim() || "Client",
        type: "client",
      });
    }

    if (isTestRun) {
      tags.push({ name: "Test", type: "test" });
    }

    for (const tag of customTags) {
      tags.push({ name: tag, type: "custom" });
    }

    onConfirm({
      tags,
      clientIdentifier: isForClient ? clientName.trim() : "",
      engagementName: engagementName.trim() || undefined,
    });
  }, [isForClient, clientName, isTestRun, customTags, engagementName, onConfirm]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("tag_engagement") || "Tag This Engagement"}
          </DialogTitle>
          {activityName && (
            <p className="text-sm text-muted-foreground mt-1">{activityName}</p>
          )}
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Engagement name field */}
          {showEngagementNameField && (
            <div className="space-y-1.5">
              <Label className="font-medium">
                {t("engagement_name") || "Engagement Name"}
              </Label>
              <Input
                placeholder={t("engagement_name_placeholder") || "e.g., My Monday reflection..."}
                value={engagementName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEngagementName(e.target.value)
                }
                autoFocus
              />
            </div>
          )}
          {showMentorOptions && (
            <>
              {/* Client toggle */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserRound className="w-4 h-4 text-primary" />
                    <Label htmlFor="for-client" className="font-medium">
                      {t("running_for_client") || "Running for a client"}
                    </Label>
                  </div>
                  <Switch
                    id="for-client"
                    checked={isForClient}
                    onCheckedChange={setIsForClient}
                  />
                </div>
                {isForClient && (
                  <Input
                    placeholder={
                      t("client_name_placeholder") || "Client name or identifier"
                    }
                    value={clientName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setClientName(e.target.value)
                    }
                    className="ms-6"
                    autoFocus
                  />
                )}
              </div>

              {/* Test run toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-orange-500" />
                  <Label htmlFor="test-run" className="font-medium">
                    {t("test_run") || "Test run"}
                  </Label>
                </div>
                <Switch
                  id="test-run"
                  checked={isTestRun}
                  onCheckedChange={setIsTestRun}
                />
              </div>

              {/* Custom tags */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-gray-500" />
                  <Label className="font-medium">
                    {t("custom_tags") || "Custom tags"}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder={t("add_tag_placeholder") || "Add a tag..."}
                    value={customTagInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCustomTagInput(e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddCustomTag}
                    disabled={!customTagInput.trim()}
                  >
                    {t("add") || "Add"}
                  </Button>
                </div>
                {customTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {customTags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="gap-1 pe-1.5"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveCustomTag(tag)}
                          className="hover:bg-gray-300 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {showMentorOptions && (
          <p className="text-xs text-muted-foreground">
            {t("tagging_hint") ||
              "Tagged engagements are excluded from your personal insights and analytics."}
          </p>
        )}

        <DialogFooter>
          <Button onClick={handleConfirm} className="w-full sm:w-auto">
            {confirmLabel ?? (t("start_engagement") || "Start")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
